import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  FiPlus, FiX, FiEdit2, FiTrash2, FiCamera, FiVideo, FiBook,
  FiCheck, FiAlertTriangle, FiTool, FiRefreshCw
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_TYPE_CONFIG = {
  photobooth: { label: 'PhotoBooth Classic', icon: FiCamera, color: '#D4A574' },
  videobooth80: { label: 'VideoBooth 360° - 80cm', icon: FiVideo, color: '#6366F1' },
  videobooth100: { label: 'VideoBooth 360° - 100cm', icon: FiVideo, color: '#8B5CF6' },
  livreor: { label: "Livre d'Or Vidéo", icon: FiBook, color: '#EC4899' }
};

const STATUS_CONFIG = {
  available: { label: 'Disponible', color: 'bg-emerald-500', icon: FiCheck },
  maintenance: { label: 'En maintenance', color: 'bg-amber-500', icon: FiTool },
  reserved: { label: 'Réservé', color: 'bg-blue-500', icon: FiCamera },
  retired: { label: 'Retiré', color: 'bg-slate-400', icon: FiX }
};

export default function EquipmentPage() {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadEquipment();
  }, []);

  const loadEquipment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/equipment/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setEquipment(await response.json());
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (item) => {
    try {
      const method = item.id ? 'PUT' : 'POST';
      const url = item.id 
        ? `${API_URL}/api/equipment/${item.id}`
        : `${API_URL}/api/equipment/`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(item)
      });

      if (response.ok) {
        toast.success(item.id ? 'Équipement mis à jour' : 'Équipement ajouté');
        setShowAddModal(false);
        setEditItem(null);
        loadEquipment();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Retirer cet équipement ? Il sera marqué comme "Retiré" mais conservé dans l\'historique.')) return;

    try {
      const response = await fetch(`${API_URL}/api/equipment/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Équipement retiré');
        loadEquipment();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredEquipment = equipment.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) return false;
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterStatus === 'all' && item.status === 'retired') return false; // Hide retired by default
    return true;
  });

  // Group by type
  const groupedEquipment = filteredEquipment.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: equipment.filter(e => e.status !== 'retired').length,
    available: equipment.filter(e => e.status === 'available').length,
    maintenance: equipment.filter(e => e.status === 'maintenance').length,
    reserved: equipment.filter(e => e.status === 'reserved').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="equipment-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Équipements</h1>
          <p className="text-slate-600 mt-1">Gérez vos machines (PhotoBooth 1, 2, 3...)</p>
        </div>
        <Button onClick={() => { setEditItem(null); setShowAddModal(true); }}>
          <FiPlus className="mr-2 h-4 w-4" />
          Ajouter une machine
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total machines</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">{stats.available}</p>
              <p className="text-sm text-slate-500">Disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.maintenance}</p>
              <p className="text-sm text-slate-500">En maintenance</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.reserved}</p>
              <p className="text-sm text-slate-500">Réservés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
        >
          <option value="all">Tous les types</option>
          {Object.entries(EQUIPMENT_TYPE_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
        >
          <option value="all">Actifs uniquement</option>
          <option value="available">Disponibles</option>
          <option value="maintenance">En maintenance</option>
          <option value="reserved">Réservés</option>
          <option value="retired">Retirés</option>
        </select>
        <Button variant="ghost" size="sm" onClick={loadEquipment}>
          <FiRefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Equipment by Type */}
      <div className="space-y-6">
        {Object.entries(groupedEquipment).map(([type, items]) => {
          const typeConfig = EQUIPMENT_TYPE_CONFIG[type] || { label: type, icon: FiCamera, color: '#666' };
          const TypeIcon = typeConfig.icon;
          
          return (
            <Card key={type}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3">
                  <div 
                    className="p-2 rounded-lg text-white"
                    style={{ backgroundColor: typeConfig.color }}
                  >
                    <TypeIcon className="h-5 w-5" />
                  </div>
                  {typeConfig.label}
                  <span className="text-sm font-normal text-slate-500">({items.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item) => {
                    const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.available;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                              style={{ backgroundColor: item.color || typeConfig.color }}
                            >
                              {item.name.match(/\d+/)?.[0] || '1'}
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">{item.name}</h4>
                              {item.serial_number && (
                                <p className="text-xs text-slate-400">S/N: {item.serial_number}</p>
                              )}
                              <div className="flex items-center gap-1 mt-1">
                                <span className={`w-2 h-2 rounded-full ${statusConfig.color}`} />
                                <span className="text-xs text-slate-500">{statusConfig.label}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => { setEditItem(item); setShowAddModal(true); }}
                              className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {item.notes && (
                          <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                            {item.notes}
                          </p>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {Object.keys(groupedEquipment).length === 0 && (
          <div className="text-center py-12 text-slate-500">
            Aucun équipement trouvé
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showAddModal && (
          <EquipmentModal
            item={editItem}
            onSave={handleSave}
            onClose={() => { setShowAddModal(false); setEditItem(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function EquipmentModal({ item, onSave, onClose }) {
  const [formData, setFormData] = useState(
    item || { type: 'photobooth', name: '', serial_number: '', status: 'available', notes: '', color: '' }
  );

  // Auto-generate name based on type
  const handleTypeChange = (type) => {
    const typeConfig = EQUIPMENT_TYPE_CONFIG[type];
    const baseName = typeConfig?.label?.split(' ')[0] || 'Machine';
    setFormData({ 
      ...formData, 
      type, 
      color: typeConfig?.color || '#666',
      name: formData.name || `${baseName} 1`
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Le nom est requis');
      return;
    }
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">
              {item ? 'Modifier' : 'Ajouter'} un équipement
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              <FiX className="h-5 w-5" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label>Type d'équipement *</Label>
            <select
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
              disabled={!!item}
            >
              {Object.entries(EQUIPMENT_TYPE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Nom de la machine *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="PhotoBooth 1"
            />
            <p className="text-xs text-slate-500 mt-1">
              Ex: PhotoBooth 1, PhotoBooth 2, VideoBooth 80cm - Paris...
            </p>
          </div>

          <div>
            <Label>Numéro de série</Label>
            <Input
              value={formData.serial_number || ''}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              placeholder="SN-2024-001"
            />
          </div>

          <div>
            <Label>Statut</Label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            >
              <option value="available">Disponible</option>
              <option value="maintenance">En maintenance</option>
              <option value="reserved">Réservé</option>
            </select>
          </div>

          <div>
            <Label>Notes</Label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
              rows={2}
              placeholder="Notes sur cet équipement..."
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit">
              {item ? 'Mettre à jour' : 'Ajouter'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
